import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          "https://citi-mobile-app.onrender.com/admin/transactions"
        );
        setTransactions(response.data);
        setLoading(false);
      } catch (error) {
        console.log('Error fetching transactions:', error);
        setError('Error fetching transactions');
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const downloadExcel = () => {
    const flattenedData = transactions.flatMap((transaction, index) => 
      transaction.products.map((product) => ({
        No: index + 1,
        CustomerName: transaction?.fullname || 'N/A',
        StoreType: transaction?.customer?.storeType || 'N/A',
        CustomerClass: transaction?.customer?.customerclass || 'N/A',
        State: transaction?.customer?.state || 'N/A',
        LGA: transaction?.customer?.lga || 'N/A',
        GPSCode: transaction?.customer?.gpsCode || 'N/A',
        AssignedAgent: transaction?.customer?.fieldagent || 'N/A',
        PhoneNumber: transaction?.phoneNumber || 'N/A',
        StoreAddress: transaction?.address || 'N/A',
        PaymentStatus: transaction?.paymentstatus || 'N/A',
        DeliveryStatus: transaction?.deliverystatus || 'N/A',
        ProductName: product.title,
        Quantity: product.quantity,
        UnitPrice: `₦${product.price.toLocaleString()}`,
        SubTotal: `₦${(product.quantity * product.price).toLocaleString()}`,
        OrderTotal: `₦${transaction.totalAmount.toLocaleString()}`,
        Date: transaction?.date ? new Date(transaction.date).toLocaleDateString() : 'N/A'
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(flattenedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'All Transactions');
    
    const maxWidth = 120;
    const colWidths = Object.keys(flattenedData[0] || {}).map(key => ({
      wch: Math.min(maxWidth, Math.max(key.length, 10))
    }));
    worksheet['!cols'] = colWidths;

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'AllTransactions.xlsx');
  };

  const downloadCSV = () => {
    const flattenedData = transactions.flatMap((transaction, index) => 
      transaction.products.map((product) => [
        index + 1,
        transaction?.fullname || '',
        transaction?.customer?.storeType || '',
        transaction?.customer?.customerclass || '',
        transaction?.customer?.state || '',
        transaction?.customer?.lga || '',
        transaction?.customer?.gpsCode || '',
        transaction?.customer?.fieldagent || '',
        transaction?.phoneNumber || '',
        transaction?.address || '',
        transaction?.paymentstatus || '',
        transaction?.deliverystatus || '',
        product.title,
        product.quantity,
        `₦${product.price}`,
        `₦${product.quantity * product.price}`,
        `₦${transaction.totalAmount}`,
        transaction?.date ? new Date(transaction.date).toLocaleDateString() : ''
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    );

    const csvHeaders = [
      'No',
      'Customer Name',
      'Store Type',
      'Customer Class',
      'State',
      'LGA',
      'GPS Code',
      'Assigned Agent',
      'Phone Number',
      'Store Address',
      'Payment Status',
      'Delivery Status',
      'Product Name',
      'Quantity',
      'Unit Price',
      'Sub Total',
      'Order Total',
      'Date'
    ].map(header => `"${header}"`).join(',');

    const csvContent = [csvHeaders, ...flattenedData].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'AllTransactions.csv');
  };

  if (loading) return <p>Loading transactions...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="max-w-full mt-20 ml-60 max-sm:ml-0 max-sm:w-screen">
      <div className="flex justify-start space-x-4 mb-8">
        <button className="px-4 py-2 rounded bg-blue-600 text-white" onClick={downloadExcel}>
          Download Excel
        </button>
        <button className="px-4 py-2 rounded bg-green-600 text-white" onClick={downloadCSV}>
          Download CSV
        </button>
      </div>

      <div className="overflow-x-auto p-3 rounded-md w-full shadow-md bg-white">
        <h1 className="text-[#1E1E1E] font-bold mb-5 text-lg lg:text-xl">
          Customer Transactions
        </h1>

        <table className="w-full border-collapse min-w-max">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3 text-left whitespace-nowrap">No</th>
              <th className="p-3 text-left whitespace-nowrap">Customer Name</th>
              <th className="p-3 text-left whitespace-nowrap">Store Type</th>
              <th className="p-3 text-left whitespace-nowrap">Customer Class</th>
              <th className="p-3 text-left whitespace-nowrap">State</th>
              <th className="p-3 text-left whitespace-nowrap">LGA</th>
              <th className="p-3 text-left whitespace-nowrap">GPS Code</th>
              <th className="p-3 text-left whitespace-nowrap">Phone Number</th>
              <th className="p-3 text-left whitespace-nowrap">Store Address</th>
              <th className="p-3 text-left whitespace-nowrap">Payment Status</th>
              <th className="p-3 text-left whitespace-nowrap">Delivery Status</th>
              <th className="p-3 text-left whitespace-nowrap">Product Name</th>
              <th className="p-3 text-left whitespace-nowrap">Quantity</th>
              <th className="p-3 text-left whitespace-nowrap">Unit Price</th>
              <th className="p-3 text-left whitespace-nowrap">Sub Total</th>
              <th className="p-3 text-left whitespace-nowrap">Order Total</th>
              <th className="p-3 text-left whitespace-nowrap">Date</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {transactions.flatMap((transaction, index) =>
              transaction.products.map((product, productIndex) => (
                <tr key={`${transaction._id}-${productIndex}`} className="border-b hover:bg-gray-50">
                  <td className="p-3 whitespace-nowrap">{index + 1}</td>
                  <td className="p-3 whitespace-nowrap">{transaction.fullname}</td>
                  <td className="p-3 whitespace-nowrap">{transaction.customer?.storeType}</td>
                  <td className="p-3 whitespace-nowrap">{transaction.customer?.customerclass}</td>
                  <td className="p-3 whitespace-nowrap">{transaction.customer?.state}</td>
                  <td className="p-3 whitespace-nowrap">{transaction.customer?.lga}</td>
                  <td className="p-3 whitespace-nowrap">{transaction.customer?.gpsCode}</td>
                  <td className="p-3 whitespace-nowrap">{transaction.phoneNumber}</td>
                  <td className="p-3 whitespace-nowrap">{transaction.address}</td>
                  <td className="p-3 whitespace-nowrap">
                    <span className={`py-1 px-3 rounded-full text-sm ${
                      transaction.paymentstatus === 'pending' ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {transaction.paymentstatus}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <span className={`py-1 px-3 rounded-full text-sm ${
                      transaction.deliverystatus === 'processing' ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {transaction.deliverystatus}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap">{product.title}</td>
                  <td className="p-3 whitespace-nowrap">{product.quantity}</td>
                  <td className="p-3 whitespace-nowrap">₦{product.price.toLocaleString()}</td>
                  <td className="p-3 whitespace-nowrap">₦{(product.quantity * product.price).toLocaleString()}</td>
                  <td className="p-3 whitespace-nowrap">₦{transaction.totalAmount.toLocaleString()}</td>
                  <td className="p-3 whitespace-nowrap">{new Date(transaction.date).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminTransactions;